import { useRef } from 'react';
import { Badge, Box, ButtonBase, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Notification_Category } from '@wavingroup/iot-core/gen/ts/wavin/blaze/v1/notification_pb';
import { NotificationCategoryCount } from '@wavingroup/iot-core/gen/ts/wavin/blaze/v1/blaze_device_service_pb';
import {
  NotificationsAlertIcon,
  NotificationsIcon,
  NotificationsReminderIcon,
  NotificationsSolvedIcon,
  NotificationsUpdatesIcon,
} from '~/shared/components/Icons/Icons';
import { notificationStyles } from '~/shared/theme/theme';

const FILTER_OPTIONS = [
  {
    type: Notification_Category.UNSPECIFIED,
    // t('notifications.filters.all')
    labelKey: 'notifications.filters.all' as const,
    Icon: NotificationsIcon,
    fontSize: 20,
  },
  {
    type: Notification_Category.ALERT,
    // t('notifications.filters.alert')
    labelKey: 'notifications.filters.alert' as const,
    Icon: NotificationsAlertIcon,
    ...notificationStyles.types.alert,
    fontSize: 26,
  },
  {
    type: Notification_Category.REMINDER,
    // t('notifications.filters.reminder')
    labelKey: 'notifications.filters.reminder' as const,
    Icon: NotificationsReminderIcon,
    ...notificationStyles.types.reminder,
    fontSize: 20,
  },
  {
    type: Notification_Category.SOLVED,
    // t('notifications.filters.solved')
    labelKey: 'notifications.filters.solved' as const,
    Icon: NotificationsSolvedIcon,
    ...notificationStyles.types.solved,
    fontSize: 20,
  },
  {
    type: Notification_Category.UPDATE,
    // t('notifications.filters.updates')
    labelKey: 'notifications.filters.updates' as const,
    Icon: NotificationsUpdatesIcon,
    ...notificationStyles.types.updates,
    fontSize: 20,
  },
];

interface NotificationFiltersProps {
  activeFilter: Notification_Category;
  onFilterChange: (filter: Notification_Category) => void;
  globalCategoryCounts?: NotificationCategoryCount[];
}

export function NotificationFilters({
  activeFilter,
  onFilterChange,
  globalCategoryCounts,
}: NotificationFiltersProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const unreadByCategory = new Map(
    globalCategoryCounts?.map((c) => [c.category, c.unreadCount]),
  );

  const totalByCategory = new Map(
    globalCategoryCounts?.map((c) => [c.category, c.totalCount]),
  );

  const visibleFilters = globalCategoryCounts?.length
    ? FILTER_OPTIONS.filter(
        ({ type }) =>
          type === Notification_Category.UNSPECIFIED ||
          (totalByCategory.get(type) ?? BigInt(0)) > BigInt(0),
      )
    : FILTER_OPTIONS;

  return (
    <Box
      ref={scrollRef}
      onWheel={(e) => {
        if (scrollRef.current && e.deltaY !== 0) {
          scrollRef.current.scrollLeft += e.deltaY;
        }
      }}
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        ml: -2,
        px: 8,
        pr: 5,
        py: 6,
        scrollbarWidth: 'none',
        justifyContent: 'center',
      }}
    >
      {visibleFilters.map(({ type, labelKey, Icon, fontSize }) => {
        const isActive = activeFilter === type;
        const showBadge = unreadByCategory.get(type);

        return (
          <Box
            key={type}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              flexShrink: 0,
            }}
          >
            <ButtonBase
              onClick={() => onFilterChange(type)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: '50%',
                border: `1px solid ${notificationStyles.filterBorderColor}`,
                mr: 1,
              }}
            >
              <Badge
                variant="dot"
                invisible={!showBadge}
                overlap="circular"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: notificationStyles.badgeColor,
                    minWidth: 6,
                    height: 6,
                    borderRadius: '50%',
                    top: '30%',
                    right: '30%',
                  },
                  '&:hover .filter-box': {
                    backgroundColor: isActive
                      ? notificationStyles.hoverColor
                      : notificationStyles.filterBackground,
                    color: !isActive
                      ? notificationStyles.hoverColor
                      : 'primary.contrastText',
                  },
                }}
              >
                <Box
                  className="filter-box"
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: isActive
                      ? 'primary.dark'
                      : notificationStyles.filterBackground,
                    color: isActive ? 'primary.contrastText' : 'primary.dark',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon
                    sx={{
                      fontSize,
                    }}
                  />
                </Box>
              </Badge>
            </ButtonBase>
            <Typography
              variant="caption"
              sx={{
                fontSize: 12,
                mt: 1,
                color: isActive ? 'text.primary' : 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {t(labelKey)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
